import { buildV2Url, resolve, Rule, stripLocale } from "./route-map";

/** Where the new app is mounted on the same origin. */
export const V2_BASE_PATH = "/v2";

export type Location = { pathname: string; search: string; hash: string };

/**
 * Which owned paths this visitor may actually be forwarded from, by their
 * locale-less rest. Every one for a signed-in family; for an anonymous
 * visitor only the pre-login ones (see forwardableFor).
 */
export type Forwardable = (rest: string) => boolean;

export const EVERY_ROUTE: Forwardable = () => true;

/**
 * The URL in the new app this location belongs to, or null to render it here.
 * Pure — the gate passes the rules it just read from the toggle, and which of
 * the paths they own this visitor may be forwarded from.
 */
export function resolveForward(
  rules: Rule[],
  location: Location,
  forwardable: Forwardable = EVERY_ROUTE,
): string | null {
  const { locale, rest } = stripLocale(location.pathname);
  const rule = resolve(rules, rest);

  if (!rule || !forwardable(rest)) {
    return null;
  }

  return buildV2Url(V2_BASE_PATH, rule, locale, location.search, location.hash);
}

/**
 * Whether a built destination still lands in the new app once the browser has
 * resolved it: same origin, and a path under the base path.
 *
 * parseRouteMap already refuses the rules that could climb out, but this is
 * the check on the string actually assembled, run just before leaving. A
 * target that resolved back into this app would forward again on arrival —
 * an infinite reload — so anything that does not end up under `/v2/` is not
 * a forward.
 */
export function leadsIntoV2(url: string, origin: string): boolean {
  try {
    const resolved = new URL(url, origin);

    return (
      resolved.origin === origin &&
      resolved.pathname.startsWith(`${V2_BASE_PATH}/`)
    );
  } catch {
    return false;
  }
}

/**
 * What appconfig's evaluate route said about this family, or that it has not
 * said anything yet.
 */
export type CutoverEvaluation =
  | { status: "pending" }
  | { status: "error" }
  | { status: "ok"; enabled: boolean; variant: string | null };

/**
 * Whether this visitor's owned routes go to the new app.
 *
 * The map decides which paths are owned; this decides whether the person
 * looking at one of them is in the arm that has been handed over. appconfig
 * rolls the variant once per user and pins it, so a family does not flip
 * between the two apps from one visit to the next.
 *
 * "hold" is a third answer and not a synonym for "render": it means the gate
 * does not yet know. The old app still renders behind it and still fires its
 * boot queries — the gate only blanks once it has a URL to leave for — so a
 * hold that resolves to "forward" costs one screen's worth of work, and one
 * that resolves to "render" costs nothing at all. What holding buys is that
 * nothing irreversible happens first: the browser does not leave, and
 * AuthProvider does not write down a return address for a path that is about
 * to belong to the other app.
 *
 * Fail closed everywhere else. An anonymous visitor gets "forward" without an
 * evaluation, because the evaluate route needs a session they do not have —
 * but that answer only reaches the screens that have no signed-in counterpart
 * here: the sign-in stack and the screens a link opens without a session
 * (forwardableFor). Those have no user to split, and nothing to protect.
 *
 * Every other owned screen stays here for an anonymous visitor, who signs in
 * on this app and then gets the evaluation like anyone else. Forwarding them
 * instead would be a loophole in the split: the new app never checks the
 * arm, so a v1-arm family whose session had expired would sign in over there
 * and stay there for every owned screen from then on.
 */
export function shouldForwardToV2({
  signedIn,
  evaluation,
}: {
  signedIn: boolean;
  evaluation: CutoverEvaluation;
}): "forward" | "hold" | "render" {
  if (!signedIn) {
    return "forward";
  }

  if (evaluation.status === "pending") {
    return "hold";
  }

  if (evaluation.status === "error" || !evaluation.enabled) {
    return "render";
  }

  // No variants configured means the toggle is a plain switch and everyone it
  // is on for goes across. A variant this app does not recognise is a map and
  // a deploy that disagree — not a reason to leave the page.
  return evaluation.variant === null || evaluation.variant === "v2"
    ? "forward"
    : "render";
}

/**
 * The destination to actually navigate to, having looked once more at where
 * the browser is, or null to drop the forward.
 *
 * A decision and the navigation that carries it out are two different moments,
 * and react-navigation can move the address bar in between: mounting a stack
 * publishes its state with the initial route first, so a "state" event fires
 * while the URL still reads the parent path. Deciding from that and then
 * leaving takes the person somewhere they never asked for — on hallosunan an
 * exact `profile` rule turned a tap on Documents into a trip to the new app's
 * Profile.
 *
 * Dropping is safe as long as it is followed by a second look. While the
 * dropped decision stands the gate renders nothing, so it waits for the bar to
 * hold still and judges that location afresh: forward if it is still owned,
 * render this app if it is not. It never sits on a blank page waiting for a
 * navigation that is not coming.
 */
export function confirmForward(
  rules: Rule[],
  target: string,
  current: Location,
  forwardable: Forwardable = EVERY_ROUTE,
): string | null {
  return resolveForward(rules, current, forwardable) === target ? target : null;
}

/**
 * What the gate needs to know about this app's own navigation. Supplied by
 * the app that mounts the gate, from its linking config; `rest` is always a
 * path with the locale segment already taken off.
 */
export type CutoverRoutes = {
  /** Whether this is where the app sends a signed-out visitor to sign in —
   *  the auth bounce off any screen that needs a session. */
  isAuthBounce: (rest: string) => boolean;
  /** Whether this route is served without a session and has no signed-in
   *  counterpart: the sign-in stack, and the screens a link opens without
   *  one. */
  isPreLogin: (rest: string) => boolean;
};

/**
 * An app that says nothing about its routes gets no bounce privilege and
 * forwards no anonymous visitor at all — the fail-closed answer.
 */
export const NO_ROUTES: CutoverRoutes = {
  isAuthBounce: () => false,
  isPreLogin: () => false,
};

/**
 * Which owned paths a visitor may be forwarded from. A signed-in family
 * already has an arm, and the rules alone decide. An anonymous visitor has
 * none, so only the pre-login screens go — see shouldForwardToV2 for why the
 * rest must not.
 */
export function forwardableFor({
  anonymous,
  routes,
}: {
  anonymous: boolean;
  routes: CutoverRoutes;
}): Forwardable {
  return anonymous ? routes.isPreLogin : EVERY_ROUTE;
}

/**
 * Whether the URL the page opened on is still the question being asked, going
 * by where the browser is: the boot URL itself, or the sign-in route this app
 * bounces an unauthenticated visitor to from it. That bounce happens within
 * the first frames, long before a slow toggle answers, and the first URL is
 * still where they asked to go. Anywhere else, a family who has been using
 * the page has asked a different question (see bootPrivileged).
 */
export function bootStillAsked(
  boot: Location,
  current: Location,
  routes: CutoverRoutes,
): boolean {
  if (current.pathname === boot.pathname && current.search === boot.search) {
    return true;
  }

  return routes.isAuthBounce(stripLocale(current.pathname).rest);
}

/**
 * Whether the boot URL may still forward without the usual re-check.
 *
 * Until the family has touched the page, every move of the address bar is
 * this app's own doing: it bounces an unauthenticated visitor to sign in, and
 * it re-routes a signed-in family through its own stacks while their variant
 * is still being answered. None of that changes what they asked for, so the
 * privilege holds wherever the bar has been taken.
 *
 * Once they have interacted, a move may be theirs, and forwarding them to the
 * boot URL would yank them out of a screen they are using, unsaved input and
 * all. From then on the privilege only holds while the browser is on the boot
 * URL or the sign-in bounce.
 */
export function bootPrivileged({
  interacted,
  boot,
  current,
  routes,
}: {
  interacted: boolean;
  boot: Location;
  current: Location;
  routes: CutoverRoutes;
}): boolean {
  return !interacted || bootStillAsked(boot, current, routes);
}

/**
 * Which location the gate should judge once the map finally arrives.
 *
 * While the map is pending the gate holds the URL the page booted on and
 * ignores navigation, because this app routes an unauthenticated visitor off
 * an owned path within the first frames and a slow toggle would otherwise
 * arrive to find the question already gone. The boot URL therefore wins when
 * it is itself owned and still being asked (see bootStillAsked) — that is
 * where the user asked to go.
 *
 * Otherwise the browser has moved somewhere the gate has never looked at, and
 * that move may well have been into an owned route. So the held URL gives way
 * to wherever the browser is now, and the map gets its one look at it.
 */
export function locationToEvaluate(
  rules: Rule[],
  held: Location,
  current: Location,
  routes: CutoverRoutes = NO_ROUTES,
  forwardable: Forwardable = EVERY_ROUTE,
): Location {
  return resolveForward(rules, held, forwardable) &&
    bootStillAsked(held, current, routes)
    ? held
    : current;
}

/**
 * The routes the new app owns, as last read from the toggle, which of them
 * the current visitor may be forwarded from, and whether that read has
 * happened yet.
 *
 * Module state rather than context because AuthProvider needs the answer
 * while it renders, and it must not pay for a second copy of the query — the
 * cut-over gate is the single writer and is mounted above it.
 *
 * `resolved: true` is the default: an app that never mounts the gate (the
 * caregiver app, and every native build) has nothing to wait for, and its
 * empty rule list forwards nothing. The gate's module calls
 * markForwardingPending() when it loads, which is before anything below it
 * renders.
 */
let rules: Rule[] = [];
let forwardable: Forwardable = EVERY_ROUTE;
let resolved = true;

/**
 * The path the page was opened on, for the post-login redirect.
 *
 * AuthProvider may only write down a return address once the map says who
 * owns it, and by then this app has already routed a signed-out visitor from
 * the deep link they opened to /login. So the gate's module records the path
 * when it loads, before any routing, and AuthProvider falls back to it once.
 *
 * Null where the gate never loads (the caregiver app, every native build):
 * there the stash never waits, so the current path is still the one opened.
 */
let bootPathname: string | null = null;

/**
 * Whether the session-adoption gate has had its turn this page load.
 *
 * The variant lookup has to hold while a family signed in on the new app is
 * still being adopted into this one, because until that lands they look
 * anonymous and anonymous always forwards. That hold needs an end, and the
 * end cannot be "the redux session changed" — the adoption may find nothing
 * to adopt and change nothing. It is this flag instead, set by an effect that
 * runs on every mount whether or not it adopts.
 */
let adoptionAttempted = false;

const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export function markForwardingPending() {
  if (!resolved) {
    return;
  }

  rules = [];
  forwardable = EVERY_ROUTE;
  resolved = false;
  notify();
}

export function setForwardedRules(
  next: Rule[],
  nextForwardable: Forwardable = EVERY_ROUTE,
) {
  rules = next;
  forwardable = nextForwardable;
  resolved = true;
  notify();
}

export function markSessionAdoptionAttempted() {
  if (adoptionAttempted) {
    return;
  }

  adoptionAttempted = true;
  notify();
}

export function sessionAdoptionAttempted() {
  return adoptionAttempted;
}

export function recordBootPathname(pathname: string) {
  bootPathname = pathname;
}

/**
 * Hands the boot path over once and forgets it. A later logout in the same
 * page load must not send the family back to where the page happened to open.
 */
export function consumeBootPathname(): string | null {
  const pathname = bootPathname;
  bootPathname = null;
  return pathname;
}

export function subscribeForwarding(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/** Snapshot for useSyncExternalStore: monotonic, so it settles after one flip. */
export function forwardingResolved() {
  return resolved;
}

/**
 * Whether the gate forwards this path for the current visitor. An owned
 * screen an anonymous visitor is kept on here is not forwarded for them, so
 * AuthProvider may remember it: they sign in on this app and come back to it,
 * and the evaluation decides from there.
 */
export function isForwardedPathname(pathname: string): boolean {
  const { rest } = stripLocale(pathname);

  return resolve(rules, rest) !== null && forwardable(rest);
}

/**
 * Whether the post-login redirect may remember this path.
 *
 * Two ways to answer no: the path belongs to the new app, or the map has not
 * arrived yet and this app cannot tell. Stashing during that window is the
 * race — the toggle request takes a moment, AuthProvider renders immediately,
 * and by the time the gate forwards an owned path the old app has already
 * written it down as where to come back to.
 */
export function shouldStashRedirection(pathname: string): boolean {
  return resolved && !isForwardedPathname(pathname);
}

/**
 * The return address AuthProvider should write down, or null to leave the
 * stash as it is.
 *
 * Nothing while the map is pending. After that, the path the browser is on if
 * it is worth coming back to; otherwise the path the page opened on, which is
 * the deep link a signed-out visitor asked for before this app moved them to
 * /login while the map was still on its way. Either way, never a path the
 * gate forwards.
 *
 * The boot path is spent on the first answer, signed in or not. A family
 * signed in at boot has no use for it, and keeping it would let a logout
 * later in the same page load stash wherever the page happened to open.
 *
 * `isValid` is passed in rather than imported so this stays platform-free:
 * the web implementation is the only one that ever says yes.
 */
export function redirectionToStash({
  signedIn,
  pathname,
  isValid,
}: {
  signedIn: boolean;
  pathname: string;
  isValid: (pathname: string) => boolean;
}): string | null {
  if (!resolved) {
    return null;
  }

  const boot = consumeBootPathname();

  if (signedIn) {
    return null;
  }

  const candidate = isValid(pathname)
    ? pathname
    : boot !== null && isValid(boot)
    ? boot
    : null;

  return candidate !== null && shouldStashRedirection(candidate)
    ? candidate
    : null;
}
